import { z } from "zod";

export const AUDIENCE_OPERATOR = {
	equals: "equals",
	notEquals: "not_equals",
	isOneOf: "is_one_of",
	isNotOneOf: "is_not_one_of",
	contains: "contains",
	notContains: "not_contains",
	matchesRegex: "matches_regex",
	notMatchesRegex: "not_matches_regex",
	greaterThan: "greater_than",
	lessThan: "less_than",
	greaterThanOrEqual: "greater_than_or_equal",
	lessThanOrEqual: "less_than_or_equal",
	between: "between",
	isSet: "is_set",
	isNotSet: "is_not_set",
} as const;

export type AudienceOperator =
	(typeof AUDIENCE_OPERATOR)[keyof typeof AUDIENCE_OPERATOR];

export const audienceOperatorSchema = z.enum([
	"equals",
	"not_equals",
	"is_one_of",
	"is_not_one_of",
	"contains",
	"not_contains",
	"matches_regex",
	"not_matches_regex",
	"greater_than",
	"less_than",
	"greater_than_or_equal",
	"less_than_or_equal",
	"between",
	"is_set",
	"is_not_set",
]);

export const audienceConditionSchema = z.object({
	fieldKey: z.string().min(1),
	paramKey: z.string().optional(),
	operator: audienceOperatorSchema,
	value: z.union([
		z.string(),
		z.array(z.string()),
		z.number(),
		z.boolean(),
		z.object({ min: z.number(), max: z.number() }),
	]),
});

export const audienceConditionGroupSchema = z.object({
	include: z.boolean(),
	matchMode: z.enum(["ALL", "ANY"]),
	conditions: z.array(audienceConditionSchema),
});

export const audienceConditionsSchema = z.object({
	matchMode: z.enum(["ALL", "ANY"]),
	groups: z.array(audienceConditionGroupSchema),
});

export type AudienceCondition = z.infer<typeof audienceConditionSchema>;
export type AudienceConditionGroup = z.infer<typeof audienceConditionGroupSchema>;
export type AudienceConditions = z.infer<typeof audienceConditionsSchema>;

export function parseAudienceConditions(
	value: unknown,
): AudienceConditions | null {
	if (value == null) {
		return null;
	}
	const parsed = audienceConditionsSchema.safeParse(value);
	return parsed.success ? parsed.data : null;
}

export function isEmptyAudienceConditions(
	conditions: AudienceConditions | null | undefined,
): boolean {
	if (!conditions) {
		return true;
	}
	return conditions.groups.length === 0;
}

export function createEmptyAudienceConditions(): AudienceConditions {
	return {
		matchMode: "ALL",
		groups: [],
	};
}
